import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { productsService } from '../services/products.service';
import type { UpdateProductData } from '../types/products.types';
import type { ImageOrderData } from '@/lib/types';

export function useUpdateProduct() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({
      productRefId,
      shopRefId,
      data,
    }: {
      productRefId: string;
      shopRefId: string;
      data: Partial<UpdateProductData>;
    }) => productsService.updateProduct(productRefId, shopRefId, data),
    onSuccess: (_, { productRefId }) => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['product', productRefId] });
      // Note: Navigation and success toast are now handled by the caller
      // to support sequential image updates
    },
    onError: (error) => {
      console.error('Failed to update product:', error);
      toast.error(t('common.error', { defaultValue: 'An error occurred' }));
    },
  });
}

export function useUpdateProductWithNavigation() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({
      productRefId,
      shopRefId,
      data,
    }: {
      productRefId: string;
      shopRefId: string;
      data: Partial<UpdateProductData>;
    }) => productsService.updateProduct(productRefId, shopRefId, data),
    onSuccess: (_, { productRefId }) => {
      toast.success(t('products.productUpdated', { defaultValue: 'Product updated successfully' }));
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['product', productRefId] });
      navigate(`/products/${productRefId}`);
    },
    onError: (error) => {
      console.error('Failed to update product:', error);
      toast.error(t('common.error', { defaultValue: 'An error occurred' }));
    },
  });
}

export function useUpdateProductImages() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      productRefId,
      images,
      orders,
    }: {
      productRefId: string;
      images: File[];
      orders: ImageOrderData[];
    }) => productsService.updateProductImages(productRefId, images, orders),
    onSuccess: (_, { productRefId }) => {
      queryClient.invalidateQueries({ queryKey: ['product', productRefId] });
      queryClient.invalidateQueries({ queryKey: ['products'] });
      // Note: Success toast is now handled by the caller to support combined product+images updates
    },
    onError: (error) => {
      console.error('Failed to update product images:', error);
      // Note: Error toast is handled by the caller for better context
    },
  });
}

export function useUpdateProductStatus() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({ productId, status }: { productId: string; status: string }) =>
      productsService.updateProductStatus(productId, status),
    onSuccess: (_, { productId }) => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['product', productId] });
    },
    onError: (error) => {
      console.error('Failed to update product status:', error);
      toast.error(t('common.error', { defaultValue: 'An error occurred' }));
    },
  });
}
